/**
 * Responsive utilities
 * Provides helpers for responsive design and media queries
 */

import { breakpoints, mediaQueries, type Breakpoint } from '../tokens/breakpoints'

/**
 * Check if current viewport matches breakpoint
 */
export const useMediaQuery = (query: string): boolean => {
  if (typeof window === 'undefined') return false

  const mediaQuery = window.matchMedia(query)
  return mediaQuery.matches
}

/**
 * Get current breakpoint
 */
export const getCurrentBreakpoint = (): Breakpoint => {
  if (typeof window === 'undefined') return 'xs'

  const width = window.innerWidth

  if (width >= Number.parseInt(breakpoints['xxl'])) return 'xxl'
  if (width >= Number.parseInt(breakpoints.xl)) return 'xl'
  if (width >= Number.parseInt(breakpoints.lg)) return 'lg'
  if (width >= Number.parseInt(breakpoints.md)) return 'md'
  if (width >= Number.parseInt(breakpoints.sm)) return 'sm'
  return 'xs'
}

/**
 * Check if viewport is at or above breakpoint
 */
export const isBreakpointUp = (breakpoint: Breakpoint): boolean => {
  if (typeof window === 'undefined') return false

  const width = window.innerWidth
  return width >= Number.parseInt(breakpoints[breakpoint])
}

/**
 * Check if viewport is below breakpoint
 */
export const isBreakpointDown = (breakpoint: Breakpoint): boolean => {
  if (typeof window === 'undefined') return false

  const width = window.innerWidth
  return width < Number.parseInt(breakpoints[breakpoint])
}

/**
 * Check if viewport is between breakpoints
 */
export const isBreakpointBetween = (min: Breakpoint, max: Breakpoint): boolean => {
  if (typeof window === 'undefined') return false

  const width = window.innerWidth
  return width >= Number.parseInt(breakpoints[min]) && width < Number.parseInt(breakpoints[max])
}

/**
 * Responsive value resolver
 * Resolves responsive values based on current breakpoint
 */
export const resolveResponsiveValue = <T>(
  value: T | Partial<Record<Breakpoint, T>>,
  currentBreakpoint?: Breakpoint
): T => {
  if (typeof value !== 'object' || value === null) {
    return value as T
  }

  const bp = currentBreakpoint || getCurrentBreakpoint()
  const responsiveValue = value as Partial<Record<Breakpoint, T>>

  // Find the best matching value by checking breakpoints in descending order
  const breakpointOrder: Breakpoint[] = ['xxl', 'xl', 'lg', 'md', 'sm', 'xs']
  const currentIndex = breakpointOrder.indexOf(bp)

  // Check current and smaller breakpoints
  for (let i = currentIndex; i < breakpointOrder.length; i++) {
    const checkBp = breakpointOrder[i]
    if (checkBp && responsiveValue[checkBp] !== undefined) {
      return responsiveValue[checkBp] as T
    }
  }

  // Fallback to any available value
  const availableValues = Object.values(responsiveValue).filter(v => v !== undefined)
  return availableValues[0] as T
}

/**
 * Create responsive CSS styles
 */
export const createResponsiveStyles = <T extends Record<string, any>>(
  styles: Partial<Record<Breakpoint, T>>
): Record<string, T> => {
  const result: Record<string, T> = {}

  Object.entries(styles).forEach(([breakpoint, style]) => {
    const bp = breakpoint as Breakpoint
    const mediaQuery = mediaQueries[bp]

    if (bp === 'xs') {
      // Base styles (no media query needed)
      Object.assign(result, style)
    } else {
      result[mediaQuery] = style
    }
  })

  return result
}

/**
 * Responsive grid utilities
 */
export const createResponsiveGrid = (
  columns: Partial<Record<Breakpoint, number>>,
  gap?: Partial<Record<Breakpoint, string>>
) => {
  const gridStyles: Record<string, any> = {}

  Object.entries(columns).forEach(([breakpoint, cols]) => {
    const bp = breakpoint as Breakpoint
    const mediaQuery = bp === 'xs' ? '' : mediaQueries[bp]

    const style = {
      display: 'grid',
      gridTemplateColumns: `repeat(${cols}, 1fr)`,
      ...(gap?.[bp] && { gap: gap[bp] }),
    }

    if (mediaQuery) {
      gridStyles[mediaQuery] = style
    } else {
      Object.assign(gridStyles, style)
    }
  })

  return gridStyles
}

/**
 * Container query utilities (experimental)
 */
export const containerQueries = {
  sm: '@container (min-width: 384px)',
  md: '@container (min-width: 448px)',
  lg: '@container (min-width: 512px)',
  xl: '@container (min-width: 768px)',
  'xxl': '@container (min-width: 1024px)',
} as const

/**
 * Responsive text utilities
 */
export const responsiveText = {
  // Responsive font sizes
  fontSize: (sizes: Partial<Record<Breakpoint, string>>) =>
    createResponsiveStyles(
      Object.fromEntries(Object.entries(sizes).map(([bp, value]) => [bp, { fontSize: value }]))
    ),

  // Responsive line heights
  lineHeight: (heights: Partial<Record<Breakpoint, string>>) =>
    createResponsiveStyles(
      Object.fromEntries(Object.entries(heights).map(([bp, value]) => [bp, { lineHeight: value }]))
    ),

  // Responsive text alignment
  textAlign: (alignments: Partial<Record<Breakpoint, 'left' | 'center' | 'right' | 'justify'>>) =>
    createResponsiveStyles(
      Object.fromEntries(
        Object.entries(alignments).map(([bp, value]) => [bp, { textAlign: value }])
      )
    ),
}

/**
 * Responsive spacing utilities
 */
export const responsiveSpacing = {
  // Responsive padding
  padding: (values: Partial<Record<Breakpoint, string>>) =>
    createResponsiveStyles(
      Object.fromEntries(Object.entries(values).map(([bp, value]) => [bp, { padding: value }]))
    ),

  // Responsive margin
  margin: (values: Partial<Record<Breakpoint, string>>) =>
    createResponsiveStyles(
      Object.fromEntries(Object.entries(values).map(([bp, value]) => [bp, { margin: value }]))
    ),

  // Responsive gap
  gap: (values: Partial<Record<Breakpoint, string>>) =>
    createResponsiveStyles(
      Object.fromEntries(Object.entries(values).map(([bp, value]) => [bp, { gap: value }]))
    ),
}
